import React, { useState, useMemo, useCallback } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";

import {
  Triangle,
  Calculator,
  ChevronRight,
  Home,
  Info,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  RotateCcw,
  Play,
  TrendingUp,
  TrendingDown,
  Target,
  Waves
} from 'lucide-react';
import "@/App.css";

// Constants
const PI = Math.PI;
const RADIANS_TO_DEGREES = 180 / PI;
const DEGREES_TO_RADIANS = PI / 180;

// Generate chart data for trig functions - REACTIVE TO ANGLE
const generateChartData = (func, currentAngle, unit, range = [-2 * PI, 2 * PI], steps = 200) => {
  const data = [];
  const step = (range[1] - range[0]) / steps;
  const angleInRadians = unit === 'degrees' ? currentAngle * DEGREES_TO_RADIANS : currentAngle;
  
  for (let x = range[0]; x <= range[1]; x += step) {
    const y = func(x);
    if (isFinite(y) && Math.abs(y) < 10) {
      data.push({ 
        x: x * RADIANS_TO_DEGREES, 
        y: parseFloat(y.toFixed(4)),
        isCurrentAngle: Math.abs(x - angleInRadians) < step
      });
    }
  }
  return data;
};

// Unit Circle Component with enhanced visualization
const UnitCircle = ({ angle, unit, activeFunc }) => {
  const angleRad = unit === 'degrees' ? angle * DEGREES_TO_RADIANS : angle;
  const x = Math.cos(angleRad);
  const y = Math.sin(angleRad);
  const tanY = Math.tan(angleRad);
  
  const centerX = 100;
  const centerY = 100;
  const radius = 70;
  
  const pointX = centerX + x * radius;
  const pointY = centerY - y * radius;
  
  const tanPointY = Math.max(10, Math.min(190, centerY - tanY * radius));
  
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full max-w-[220px] mx-auto">
      {/* Background grid */}
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="hsl(var(--border))" strokeWidth="0.3" />
        </pattern>
      </defs>
      <rect width="200" height="200" fill="url(#grid)" />
      
      {/* Axis lines */}
      <line x1="0" y1="100" x2="200" y2="100" stroke="hsl(var(--border))" strokeWidth="1.5" />
      <line x1="100" y1="0" x2="100" y2="200" stroke="hsl(var(--border))" strokeWidth="1.5" />
      
      {/* Unit circle */}
      <circle 
        cx="100" 
        cy="100" 
        r="70" 
        fill="hsl(var(--primary) / 0.03)" 
        stroke="hsl(var(--muted-foreground))" 
        strokeWidth="1.5" 
      />
      
      {/* Quadrant labels */}
      <text x="155" y="55" fontSize="8" fill="hsl(var(--muted-foreground))">I</text>
      <text x="45" y="55" fontSize="8" fill="hsl(var(--muted-foreground))">II</text>
      <text x="45" y="155" fontSize="8" fill="hsl(var(--muted-foreground))">III</text>
      <text x="155" y="155" fontSize="8" fill="hsl(var(--muted-foreground))">IV</text>
      
      {/* Angle arc */}
      {angle !== 0 && (
        <path
          d={`M 120 100 A 20 20 0 ${Math.abs(angleRad) > PI ? 1 : 0} ${angleRad >= 0 ? 0 : 1} ${100 + 20 * Math.cos(angleRad)} ${100 - 20 * Math.sin(angleRad)}`}
          fill="hsl(var(--primary) / 0.1)"
          stroke="hsl(var(--primary))"
          strokeWidth="2"
        />
      )}
      
      {/* Radius line */}
      <line
        x1="100"
        y1="100"
        x2={pointX}
        y2={pointY}
        stroke="hsl(var(--primary))"
        strokeWidth="2.5"
      />
      
      {/* Sin line (vertical) - highlighted if active */}
      <line
        x1={pointX}
        y1="100"
        x2={pointX}
        y2={pointY}
        stroke={activeFunc === 'sin' ? '#ef4444' : 'rgba(239, 68, 68, 0.5)'}
        strokeWidth={activeFunc === 'sin' ? 3 : 2}
        strokeDasharray={activeFunc === 'sin' ? '0' : '4 2'}
      />
      
      {/* Cos line (horizontal) - highlighted if active */}
      <line
        x1="100"
        y1={pointY}
        x2={pointX}
        y2={pointY}
        stroke={activeFunc === 'cos' ? '#3b82f6' : 'rgba(59, 130, 246, 0.5)'}
        strokeWidth={activeFunc === 'cos' ? 3 : 2}
        strokeDasharray={activeFunc === 'cos' ? '0' : '4 2'}
      />
      
      {/* Tangent visualization (when tan is active) */}
      {activeFunc === 'tan' && isFinite(tanY) && Math.abs(tanY) < 3 && (
        <>
          <line
            x1={centerX + radius}
            y1="100"
            x2={centerX + radius}
            y2={tanPointY}
            stroke="#22c55e"
            strokeWidth="3"
          />
          <circle cx={centerX + radius} cy={tanPointY} r="4" fill="#22c55e" />
        </>
      )}
      
      {/* Point on circle */}
      <circle 
        cx={pointX} 
        cy={pointY} 
        r="7" 
        fill="hsl(var(--primary))" 
        stroke="hsl(var(--background))"
        strokeWidth="2"
      />
      
      {/* Origin point */}
      <circle cx="100" cy="100" r="3" fill="hsl(var(--foreground))" />
      
      {/* Axis labels */}
      <text x="188" y="95" fontSize="10" fill="hsl(var(--muted-foreground))" fontWeight="500">x</text>
      <text x="105" y="12" fontSize="10" fill="hsl(var(--muted-foreground))" fontWeight="500">y</text>
      
      {/* Key points */}
      <text x="168" y="115" fontSize="8" fill="hsl(var(--muted-foreground))">(1,0)</text>
      <text x="105" y="35" fontSize="8" fill="hsl(var(--muted-foreground))">(0,1)</text>
      
      {/* Coordinate labels */}
      <rect 
        x={pointX + 8} 
        y={pointY - 24} 
        width="60" 
        height="18" 
        rx="4" 
        fill="hsl(var(--card))" 
        stroke="hsl(var(--border))"
        strokeWidth="0.5"
      />
      <text x={pointX + 12} y={pointY - 11} fontSize="9" fill="hsl(var(--foreground))" fontFamily="monospace">
        ({x.toFixed(2)}, {y.toFixed(2)})
      </text>
    </svg>
  );
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Interactive Slider for angle input
const AngleSlider = ({ value, onChange, unit, min, max }) => {
  return (
    <div className="relative w-full">
      <input
        type="range"
        min={min}
        max={max}
        step={unit === 'degrees' ? 1 : 0.01}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
        data-testid="angle-slider"
      />
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>{min}{unit === 'degrees' ? '°' : ''}</span>
        <span>{max}{unit === 'degrees' ? '°' : ''}</span>
      </div>
    </div>
  );
};

// Graph Explanation Component
const GraphExplanation = ({ activeFunc, angle, unit, trigValue }) => {
  const angleInDeg = unit === 'degrees' ? angle : angle * RADIANS_TO_DEGREES;
  const normalizedAngle = ((angleInDeg % 360) + 360) % 360;
  
  let quadrant = 1;
  if (normalizedAngle > 90 && normalizedAngle <= 180) quadrant = 2;
  else if (normalizedAngle > 180 && normalizedAngle <= 270) quadrant = 3;
  else if (normalizedAngle > 270 && normalizedAngle < 360) quadrant = 4;
  
  const getTrend = () => {
    if (activeFunc === 'sin') {
      if (normalizedAngle < 90) return { direction: 'increasing', icon: TrendingUp, desc: 'rising from 0 toward 1' };
      if (normalizedAngle < 180) return { direction: 'decreasing', icon: TrendingDown, desc: 'falling from 1 toward 0' };
      if (normalizedAngle < 270) return { direction: 'decreasing', icon: TrendingDown, desc: 'falling from 0 toward -1' };
      return { direction: 'increasing', icon: TrendingUp, desc: 'rising from -1 toward 0' };
    }
    if (activeFunc === 'cos') {
      if (normalizedAngle < 90) return { direction: 'decreasing', icon: TrendingDown, desc: 'falling from 1 toward 0' };
      if (normalizedAngle < 180) return { direction: 'decreasing', icon: TrendingDown, desc: 'falling from 0 toward -1' };
      if (normalizedAngle < 270) return { direction: 'increasing', icon: TrendingUp, desc: 'rising from -1 toward 0' };
      return { direction: 'increasing', icon: TrendingUp, desc: 'rising from 0 toward 1' };
    }
    if (activeFunc === 'tan') {
      if (normalizedAngle === 90 || normalizedAngle === 270) return { direction: 'undefined', icon: AlertCircle, desc: 'undefined (vertical asymptote)' };
      return { direction: 'increasing', icon: TrendingUp, desc: 'always increasing between asymptotes' };
    }
    return { direction: 'increasing', icon: TrendingUp, desc: 'varies based on function' };
  };
  
  const trend = getTrend();
  const TrendIcon = trend.icon;
  
  const getRealWorldMeaning = () => {
    if (!isFinite(trigValue)) return 'The value is undefined at this angle.';
    
    const absValue = Math.abs(trigValue);
    const valueDesc = absValue < 0.1 ? 'very small' : absValue < 0.5 ? 'moderate' : absValue < 0.9 ? 'large' : 'near maximum';
    
    if (activeFunc === 'sin') {
      return `The vertical height on the unit circle is ${trigValue > 0 ? 'above' : 'below'} the x-axis. A ${valueDesc} value (${trigValue.toFixed(4)}) means the point is ${absValue > 0.7 ? 'far from' : 'close to'} the horizontal axis.`;
    }
    if (activeFunc === 'cos') {
      return `The horizontal distance on the unit circle is to the ${trigValue > 0 ? 'right' : 'left'} of the y-axis. A ${valueDesc} value (${trigValue.toFixed(4)}) means the point is ${absValue > 0.7 ? 'far from' : 'close to'} the vertical axis.`;
    }
    if (activeFunc === 'tan') {
      return `The slope of the line from origin to the point is ${trigValue > 0 ? 'positive (upward)' : 'negative (downward)'}. A value of ${trigValue.toFixed(4)} means ${absValue > 2 ? 'a very steep' : absValue > 1 ? 'a steep' : absValue > 0.5 ? 'a moderate' : 'a gentle'} slope.`;
    }
    return 'This is a reciprocal function value.';
  };
  
  return (
    <div className="space-y-3 p-4 bg-muted/30 rounded-xl" data-testid="graph-explanation">
      <h4 className="font-semibold text-foreground flex items-center gap-2">
        <Waves className="w-4 h-4 text-primary" />
        Graph Reading Explanation
      </h4>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2">
          <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">Current Position:</span>
            <p className="text-muted-foreground">
              At {angleInDeg.toFixed(1)}° (Quadrant {quadrant}), {activeFunc}(θ) = <span className="font-mono text-foreground">{isFinite(trigValue) ? trigValue.toFixed(4) : 'undefined'}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <TrendIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${trend.direction === 'increasing' ? 'text-green-500' : trend.direction === 'decreasing' ? 'text-red-500' : 'text-yellow-500'}`} />
          <div>
            <span className="font-medium">Trend:</span>
            <p className="text-muted-foreground">
              The function is <span className="font-medium text-foreground">{trend.desc}</span> at this angle.
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">What This Means:</span>
            <p className="text-muted-foreground">{getRealWorldMeaning()}</p>
          </div>
        </div>
        
        <div className="mt-3 p-3 bg-background rounded-lg">
          <div className="text-xs text-muted-foreground mb-1">Value Scale</div>
          <div className="h-2 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full relative">
            {isFinite(trigValue) && (
              <div 
                className="absolute w-3 h-3 bg-white border-2 border-primary rounded-full -top-0.5 transform -translate-x-1/2 transition-all duration-300"
                style={{ left: `${((trigValue + 1) / 2) * 100}%` }}
              />
            )}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>-1</span>
            <span>0</span>
            <span>+1</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Calculator Component
function TrigCalculator() {
  const [angle, setAngle] = useState(45);
  const [unit, setUnit] = useState('degrees');
  const [activeFunc, setActiveFunc] = useState('sin');
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);
  const [isAnimating, setIsAnimating] = useState(false);

  const angleInRadians = useMemo(() => {
    return unit === 'degrees' ? angle * DEGREES_TO_RADIANS : angle;
  }, [angle, unit]);

  const trigValues = useMemo(() => {
    const rad = angleInRadians;
    return {
      sin: Math.sin(rad),
      cos: Math.cos(rad),
      tan: Math.tan(rad),
      csc: 1 / Math.sin(rad),
      sec: 1 / Math.cos(rad),
      cot: 1 / Math.tan(rad),
    };
  }, [angleInRadians]);

  const displayAngleInDeg = unit === 'degrees' ? angle : (angle * RADIANS_TO_DEGREES);

  const chartData = useMemo(() => ({
    sin: generateChartData(Math.sin, angle, unit),
    cos: generateChartData(Math.cos, angle, unit),
    tan: generateChartData(Math.tan, angle, unit),
  }), [angle, unit]);

  const formatValue = useCallback((val) => {
    if (!isFinite(val)) return 'undefined';
    return val.toFixed(6);
  }, []);

  const handleReset = useCallback(() => {
    setAngle(45);
    setUnit('degrees');
    setActiveFunc('sin');
  }, []);

  const handleAnimate = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    let currentAngle = 0;
    const interval = setInterval(() => {
      currentAngle += 5;
      setAngle(currentAngle);
      if (currentAngle >= 360) {
        clearInterval(interval);
        setIsAnimating(false);
      }
    }, 50);
  }, [isAnimating]);

  const displayAngle = unit === 'degrees' ? angle : angle.toFixed(4);

  const chartColors = {
    sin: '#ef4444',
    cos: '#3b82f6',
    tan: '#22c55e',
  };

  return (
    <div className="container mx-auto px-4 py-8" data-testid="trig-calculator">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Triangle className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Trigonometry Calculator</h1>
            <p className="text-muted-foreground">Calculate sin, cos, tan and visualize on unit circle</p>
          </div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Calculator Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:sticky lg:top-24 lg:self-start"
        >
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                <span className="font-semibold text-foreground">Calculator</span>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={handleReset} 
                  title="Reset"
                  data-testid="reset-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={handleAnimate} 
                  disabled={isAnimating}
                  title="Animate"
                  data-testid="animate-btn"
                >
                  <Play className={`w-4 h-4 ${isAnimating ? 'animate-pulse' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Angle Input */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Enter Angle</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={angle}
                    onChange={(e) => setAngle(parseFloat(e.target.value) || 0)}
                    className="border text-xl font-mono h-14"
                    placeholder="45"
                    data-testid="angle-input"
                  />
                </div>
              </div>

              {/* Angle Slider */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Drag to adjust</Label>
                <AngleSlider 
                  value={angle} 
                  onChange={setAngle} 
                  unit={unit}
                  min={unit === 'degrees' ? 0 : 0}
                  max={unit === 'degrees' ? 360 : parseFloat((2 * PI).toFixed(2))}
                />
              </div>

              {/* Unit Toggle */}
              <Tabs value={unit} onValueChange={setUnit}>
                <TabsList className="w-full">
                  <TabsTrigger value="degrees" className="flex-1" data-testid="degrees-tab">Degrees (°)</TabsTrigger>
                  <TabsTrigger value="radians" className="flex-1" data-testid="radians-tab">Radians</TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Quick Angles */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Quick Angles</Label>
                <div className="flex flex-wrap gap-2">
                  {[0, 30, 45, 60, 90, 180, 270, 360].map((a) => (
                    <Button
                      key={a}
                      variant="outline"
                      size="sm"
                      className={`text-xs rounded-lg transition-all ${angle === a && unit === 'degrees' ? 'bg-primary text-primary-foreground border-primary' : ''}`}
                      onClick={() => {
                        setAngle(a);
                        setUnit('degrees');
                      }}
                      data-testid={`quick-angle-${a}`}
                    >
                      {a}°
                    </Button>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="pt-4 border-t border-border space-y-3">
                <Label className="text-sm font-medium text-foreground">Results</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(trigValues).map(([func, value]) => (
                    <motion.div
                      key={func}
                      className={`rounded-xl p-3 cursor-pointer transition-all border ${activeFunc === func ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30'}`}
                      onClick={() => setActiveFunc(func)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid={`result-${func}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground font-medium">{func}(θ)</span>
                        <CopyButton text={formatValue(value)} />
                      </div>
                      <span className="font-mono text-sm font-semibold text-foreground block truncate">
                        {formatValue(value)}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Center/Right Column - Visualization & Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visualization Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Info className="w-4 h-4 text-primary" />
                  Interactive Visualization
                  <Badge variant="secondary" className="ml-2 text-xs">Real-time</Badge>
                </h2>
                <Tabs value={activeFunc} onValueChange={setActiveFunc}>
                  <TabsList>
                    <TabsTrigger value="sin" className="text-xs" style={{ color: activeFunc === 'sin' ? '#ef4444' : undefined }}>sin</TabsTrigger>
                    <TabsTrigger value="cos" className="text-xs" style={{ color: activeFunc === 'cos' ? '#3b82f6' : undefined }}>cos</TabsTrigger>
                    <TabsTrigger value="tan" className="text-xs" style={{ color: activeFunc === 'tan' ? '#22c55e' : undefined }}>tan</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Unit Circle */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Unit Circle</h3>
                  <div className="rounded-xl p-4 bg-muted/30 border">
                    <UnitCircle angle={angle} unit={unit} activeFunc={activeFunc} />
                    <div className="flex justify-center gap-4 mt-3">
                      <div className="flex items-center gap-1">
                        <div className={`w-3 h-1 rounded ${activeFunc === 'sin' ? 'bg-red-500' : 'bg-red-500/50'}`} />
                        <span className="text-xs text-muted-foreground">sin</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className={`w-3 h-1 rounded ${activeFunc === 'cos' ? 'bg-blue-500' : 'bg-blue-500/50'}`} />
                        <span className="text-xs text-muted-foreground">cos</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className={`w-3 h-1 rounded ${activeFunc === 'tan' ? 'bg-green-500' : 'bg-green-500/50'}`} />
                        <span className="text-xs text-muted-foreground">tan</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Graph */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {activeFunc}(x) Graph 
                    <span className="text-xs text-primary ml-2">θ = {displayAngleInDeg.toFixed(0)}°</span>
                  </h3>
                  <div className="rounded-xl p-4 h-[200px] bg-muted/30 border" data-testid="trig-graph">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData[activeFunc]} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="x"
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          tickFormatter={(v) => `${Math.round(v)}°`}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          domain={activeFunc === 'tan' ? [-5, 5] : [-1.5, 1.5]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                          formatter={(value) => [value.toFixed(4), activeFunc]}
                          labelFormatter={(label) => `${label.toFixed(1)}°`}
                        />
                        <ReferenceLine 
                          x={parseFloat(displayAngleInDeg.toFixed(1))} 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={2}
                          strokeDasharray="5 5" 
                          label={{ 
                            value: 'θ', 
                            position: 'top', 
                            fill: 'hsl(var(--primary))',
                            fontSize: 12
                          }}
                        />
                        <ReferenceLine y={trigValues[activeFunc]} stroke={chartColors[activeFunc]} strokeDasharray="3 3" strokeWidth={1} />
                        <Line
                          type="monotone"
                          dataKey="y"
                          stroke={chartColors[activeFunc]}
                          dot={false}
                          strokeWidth={2}
                          animationDuration={300}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    The dashed line shows your current angle (θ = {displayAngle}{unit === 'degrees' ? '°' : ' rad'})
                  </p>
                </div>
              </div>
              
              {/* Graph Explanation */}
              <div className="mt-4">
                <GraphExplanation 
                  activeFunc={activeFunc} 
                  angle={angle} 
                  unit={unit} 
                  trigValue={trigValues[activeFunc]} 
                />
              </div>
            </Card>
          </motion.div>

          {/* What Are Sin, Cos, Tan - Comprehensive Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                What Are Sin, Cos, and Tan? (Simply Explained)
              </h2>
              
              <div className="space-y-6">
                {/* SINE */}
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <h3 className="font-bold text-lg text-red-600 dark:text-red-400 mb-2">📐 Sine (sin) - The Height Measurer</h3>
                  <p className="text-muted-foreground mb-3">
                    <strong>Simple Definition:</strong> Sine tells you <em>how high</em> you are.
                  </p>
                  <div className="bg-background rounded-lg p-3 mb-3">
                    <p className="text-sm">Imagine you're on a Ferris wheel. As you rotate around:</p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      <li>• <strong>sin(0°) = 0</strong> → You're at the starting position (ground level)</li>
                      <li>• <strong>sin(90°) = 1</strong> → You're at the very top!</li>
                      <li>• <strong>sin(180°) = 0</strong> → You're back at ground level (other side)</li>
                      <li>• <strong>sin(270°) = -1</strong> → You're at the very bottom</li>
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <strong>Formula:</strong> sin(θ) = opposite side ÷ hypotenuse = <span className="font-mono">y-coordinate on unit circle</span>
                  </p>
                </div>
                
                {/* COSINE */}
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <h3 className="font-bold text-lg text-blue-600 dark:text-blue-400 mb-2">📏 Cosine (cos) - The Distance Measurer</h3>
                  <p className="text-muted-foreground mb-3">
                    <strong>Simple Definition:</strong> Cosine tells you <em>how far right or left</em> you are.
                  </p>
                  <div className="bg-background rounded-lg p-3 mb-3">
                    <p className="text-sm">Using the same Ferris wheel:</p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      <li>• <strong>cos(0°) = 1</strong> → You're all the way to the right</li>
                      <li>• <strong>cos(90°) = 0</strong> → You're neither left nor right (directly above center)</li>
                      <li>• <strong>cos(180°) = -1</strong> → You're all the way to the left</li>
                      <li>• <strong>cos(270°) = 0</strong> → Back to center horizontally</li>
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <strong>Formula:</strong> cos(θ) = adjacent side ÷ hypotenuse = <span className="font-mono">x-coordinate on unit circle</span>
                  </p>
                </div>
                
                {/* TANGENT */}
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <h3 className="font-bold text-lg text-green-600 dark:text-green-400 mb-2">📈 Tangent (tan) - The Slope Measurer</h3>
                  <p className="text-muted-foreground mb-3">
                    <strong>Simple Definition:</strong> Tangent tells you <em>how steep the slope is</em>.
                  </p>
                  <div className="bg-background rounded-lg p-3 mb-3">
                    <p className="text-sm">Think of it as "rise over run" or steepness:</p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      <li>• <strong>tan(0°) = 0</strong> → Flat, no slope</li>
                      <li>• <strong>tan(45°) = 1</strong> → Perfect 45° slope (rise = run)</li>
                      <li>• <strong>tan(90°) = undefined</strong> → Vertical line! (infinite slope)</li>
                      <li>• <strong>tan(135°) = -1</strong> → Downward 45° slope</li>
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <strong>Formula:</strong> tan(θ) = opposite ÷ adjacent = sin(θ) ÷ cos(θ) = <span className="font-mono">slope of the radius line</span>
                  </p>
                </div>
                
                {/* Quick Summary Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Angle</th>
                        <th className="text-center py-2 text-red-500">sin</th>
                        <th className="text-center py-2 text-blue-500">cos</th>
                        <th className="text-center py-2 text-green-500">tan</th>
                        <th className="text-left py-2">What it means</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b">
                        <td className="py-2">0°</td>
                        <td className="text-center">0</td>
                        <td className="text-center">1</td>
                        <td className="text-center">0</td>
                        <td>Starting point, pointing right</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">30°</td>
                        <td className="text-center">0.5</td>
                        <td className="text-center">0.87</td>
                        <td className="text-center">0.58</td>
                        <td>Gentle slope, mostly horizontal</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">45°</td>
                        <td className="text-center">0.71</td>
                        <td className="text-center">0.71</td>
                        <td className="text-center">1</td>
                        <td>Equal parts vertical & horizontal</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">60°</td>
                        <td className="text-center">0.87</td>
                        <td className="text-center">0.5</td>
                        <td className="text-center">1.73</td>
                        <td>Steep slope, mostly vertical</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">90°</td>
                        <td className="text-center">1</td>
                        <td className="text-center">0</td>
                        <td className="text-center">∞</td>
                        <td>Pointing straight up</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Step-by-Step Solution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-primary" />
                Step-by-Step Solution
              </h2>

              <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                <AccordionItem value="step-1" className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</span>
                      <span className="font-medium">Identify the angle</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-3">
                      <p className="text-muted-foreground">
                        Your input angle is <span className="font-mono text-foreground font-semibold bg-secondary px-2 py-0.5 rounded">{displayAngle}</span> {unit}.
                      </p>
                      
                      <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                        <p className="text-sm text-foreground font-medium">Conversions:</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">In degrees: </span>
                            <span className="font-mono text-foreground">{(unit === 'degrees' ? angle : angle * RADIANS_TO_DEGREES).toFixed(2)}°</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">In radians: </span>
                            <span className="font-mono text-foreground">{angleInRadians.toFixed(6)} rad</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">
                        <strong>Tip:</strong> This angle lies in Quadrant 
                        <span className="font-semibold text-primary ml-1">
                          {(() => {
                            const deg = unit === 'degrees' ? angle : angle * RADIANS_TO_DEGREES;
                            const normalized = ((deg % 360) + 360) % 360;
                            if (normalized <= 90) return 'I (where sin > 0, cos > 0)';
                            if (normalized <= 180) return 'II (where sin > 0, cos < 0)';
                            if (normalized <= 270) return 'III (where sin < 0, cos < 0)';
                            return 'IV (where sin < 0, cos > 0)';
                          })()}
                        </span>
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step-2" className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</span>
                      <span className="font-medium">Apply the trigonometric function</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-4">
                      <div className="bg-secondary/50 rounded-lg p-4">
                        <p className="text-sm font-medium text-foreground mb-3">Unit Circle Definition:</p>
                        <div className="space-y-2">
                          <div className={`bg-background rounded-lg p-2 ${activeFunc === 'sin' ? 'ring-2 ring-red-500' : ''}`}>
                            <span className="text-red-500 font-semibold">sin(θ)</span> = opposite / hypotenuse = <strong>y-coordinate</strong>
                          </div>
                          <div className={`bg-background rounded-lg p-2 ${activeFunc === 'cos' ? 'ring-2 ring-blue-500' : ''}`}>
                            <span className="text-blue-500 font-semibold">cos(θ)</span> = adjacent / hypotenuse = <strong>x-coordinate</strong>
                          </div>
                          <div className={`bg-background rounded-lg p-2 ${activeFunc === 'tan' ? 'ring-2 ring-green-500' : ''}`}>
                            <span className="text-green-500 font-semibold">tan(θ)</span> = opposite / adjacent = sin(θ) / cos(θ)
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-muted-foreground">
                        For <span className="font-mono text-foreground">θ = {displayAngle}{unit === 'degrees' ? '°' : ' rad'}</span>:
                      </p>
                      
                      <div className="bg-primary/10 rounded-lg p-3 text-lg">
                        <span className="text-primary font-semibold">{activeFunc}</span>({displayAngle}{unit === 'degrees' ? '°' : ''}) = 
                        <span className="text-primary font-bold ml-2">{formatValue(trigValues[activeFunc])}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step-3" className="border-0">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</span>
                      <span className="font-medium">All trigonometric values</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11">
                      <p className="text-sm text-muted-foreground mb-4">
                        Complete results for <span className="font-mono text-foreground">θ = {displayAngle}{unit === 'degrees' ? '°' : ' rad'}</span>:
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {Object.entries(trigValues).map(([func, value]) => (
                          <div key={func} className="bg-secondary/50 rounded-lg p-3">
                            <span className="text-xs text-muted-foreground block mb-1">
                              {func}({displayAngle}{unit === 'degrees' ? '°' : ''})
                            </span>
                            <p className="font-mono text-sm font-medium text-foreground">{formatValue(value)}</p>
                            {!isFinite(value) && (
                              <span className="text-xs text-destructive">Division by zero</span>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Verification */}
                      <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <p className="text-sm font-medium text-foreground mb-2">✓ Verification (Pythagorean Identity):</p>
                        <p className="font-mono text-sm">
                          sin²({displayAngle}°) + cos²({displayAngle}°) = 
                          <span className="text-primary font-semibold ml-2">
                            {(Math.pow(trigValues.sin, 2) + Math.pow(trigValues.cos, 2)).toFixed(6)}
                          </span>
                          <span className="text-green-500 ml-2">≈ 1 ✓</span>
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          </motion.div>

          {/* Key Identities */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Key Trigonometric Identities
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { name: 'Pythagorean Identity', formula: 'sin²(θ) + cos²(θ) = 1' },
                { name: 'Tangent Definition', formula: 'tan(θ) = sin(θ) / cos(θ)' },
                { name: 'Reciprocal (csc)', formula: 'csc(θ) = 1 / sin(θ)' },
                { name: 'Reciprocal (sec)', formula: 'sec(θ) = 1 / cos(θ)' },
                { name: 'Reciprocal (cot)', formula: 'cot(θ) = 1 / tan(θ)' },
                { name: 'Co-function', formula: 'sin(90° - θ) = cos(θ)' },
              ].map((item, i) => (
                <div key={i} className="bg-muted/30 rounded-xl p-4">
                  <span className="text-xs text-muted-foreground block mb-1">{item.name}</span>
                  <code className="font-mono text-sm text-foreground">{item.formula}</code>
                </div>
              ))}
            </div>
          </Card>

          {/* Common Mistakes */}
          <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Common Mistakes to Avoid
            </h2>
            <ul className="space-y-3">
              {[
                'Forgetting to convert between degrees and radians when your calculator is in the wrong mode',
                'Confusing the reciprocal functions: csc, sec, and cot are NOT the inverse functions (sin⁻¹, cos⁻¹, tan⁻¹)',
                'Dividing by zero: tan(90°) and cot(0°) are undefined because cos(90°) = 0 and sin(0°) = 0',
                'Sign errors: Remember that trig functions can be negative depending on the quadrant',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* FAQ Section */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  q: 'What is the difference between degrees and radians?',
                  a: 'Degrees divide a circle into 360 parts, while radians measure angles based on the radius of the circle. One full rotation is 360° or 2π radians. To convert: radians = degrees × (π/180).'
                },
                {
                  q: 'Why is tan(90°) undefined?',
                  a: 'Tangent is defined as sin(θ)/cos(θ). At 90°, cos(90°) = 0, and division by zero is undefined. Geometrically, this represents a vertical line which has no finite slope.'
                },
                {
                  q: 'What is the unit circle?',
                  a: 'The unit circle is a circle with radius 1 centered at the origin. It provides a geometric way to define trigonometric functions: for any angle θ, the point on the circle is (cos θ, sin θ).'
                },
                {
                  q: 'How are sin and cos related?',
                  a: 'Sin and cos are co-functions: sin(θ) = cos(90° - θ). They are also related by the Pythagorean identity: sin²(θ) + cos²(θ) = 1. Graphically, cos is a phase-shifted version of sin.'
                },
                {
                  q: 'When would I use csc, sec, or cot instead of sin, cos, or tan?',
                  a: 'The reciprocal functions (csc, sec, cot) are useful when working with expressions that naturally involve 1/sin, 1/cos, or 1/tan. They simplify certain calculus operations and appear in physics equations.'
                }
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-medium text-foreground">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>

          {/* Real World Applications */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
            <ul className="space-y-3">
              {[
                'Navigation and GPS systems use trigonometry to calculate distances and bearings',
                'Architecture and construction rely on trig to calculate roof pitches and structural angles',
                'Physics uses sine and cosine waves to model oscillations, sound, and light',
                'Computer graphics employ trigonometry for rotations, animations, and 3D transformations',
                'Music theory uses sine waves as the fundamental building blocks of sound synthesis',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default TrigCalculator;
